<template>
    <v-card class="wrapper ma-0 pa-4">
        <v-container class="pa-0 d-flex justify-space-between align-center">
            <v-card-text class="text-title pa-0">{{ $t('invoiceAndStockComparison') }}</v-card-text>
            <div class="d-flex gap-2">
                <v-btn
                    outlined
                    color="primary"
                    height="32"
                    v-shortkey="['f5']"
                    @click="getItems"
                    style="min-width: 35px !important;padding: 0 !important;"
                >
                    <v-icon size="16">mdi-refresh</v-icon>
                </v-btn>
                <v-btn
                  color="primary"
                  outlined
                  height="32"
                  @click="filterDialog = true"
                >
                  {{ $t('filter') }}
                  <v-icon size="16" class="ms-1">mdi-filter-outline</v-icon>
                </v-btn>
            </div>
        </v-container>
  	    <div 
          v-if="initialLoading"
          class="mt-4"
          style="height: calc(100vh - 158px);overflow: hidden;"
        >
          <v-skeleton-loader  
            type="list-item"
            v-for="n in 20"
            :key="n"
          >
          </v-skeleton-loader>
        </div>
        <AgGridRenderList
          v-else
          :columnDefs="columnDefs"
          :rowData="rowData"
          :totalRows="totalRows"
          :lastPage="lastPage"
          :loading="paginationLoading"
          @updatePagination="updatePagination"
          @updateItemPerPage="updateItemPerPage"
        />
        <!--filter dialog-->
        <v-dialog
          v-model="filterDialog"
          :fullscreen="isMobile"
          :hide-overlay="isMobile"
          width="70%"
          :draggable="true"
        >
          <v-card>
            <v-col
              class="d-flex"
            >
              <v-btn
                icon
                color="grey darken-2"
                @click="filter = false"
              >
                <v-icon
                  class="close-hover"
                  large
                >
                  mdi-close
                </v-icon>
              </v-btn>
              <p class="mx-auto font-weight-medium mt-2">
                {{ $t('filter') }}
              </p>
            </v-col>
            <v-row class="ma-0">
              <v-col
                cols="12"
                md="4"
              >
                <autocomplete
                  v-model="account"
                  :title="$t('customer')"
                  type="person"
                  :filter="true"
                  :multiple="true"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <status
                  v-model="sellStatus"
                  :hideconfig="true"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <autocomplete
                  v-model="currencyFilter"
                  :title="$t('currency')"
                  type="currency"
                  :filter="true"
                  :multiple="true"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <money
                  v-model="fromAmount"
                  :dense="true"
                  :label="$t('from') + ' ' + $t('amount')"
                  :outlined="true"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-text-field
                  v-model="fromNumber"
                  :label="$t('from') + ' ' + $t('number')"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <template>
                  <v-text-field
                    id="fromId"
                    v-model="fromDate"
                    :label="$t('from') + ' ' + $t('date')"
                    outlined
                    dense
                    clearable
                  />
                  <date-picker
                    v-model="fromDate"
                    element="fromId"
                    :locale="$store.state.date_locale"
                    :format="$store.state.date_format"
                    auto-submit
                    :color="this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                    @close="setDate"
                  />
                </template>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <money
                  v-model="toAmount"
                  :dense="true"
                  :label="$t('until') + ' ' + $t('amount')"
                  :outlined="true"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-text-field
                  v-model="toNumber"
                  :label="$t('until') + ' ' + $t('number')"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <template>
                  <v-text-field
                    id="toId"
                    v-model="toDate"
                    :label="$t('until') + ' ' + $t('date')"
                    outlined
                    dense
                    clearable
                  />
                  <date-picker
                    v-model="toDate"
                    element="toId"
                    :locale="$store.state.date_locale"
                    :format="$store.state.date_format"
                    :min="fromDate"
                    auto-submit
                    :color="this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                    @close="setDate"
                  />
                </template>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <tag
                  v-model="tag"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <autocomplete
                  v-model="category"
                  :title="$t('category')"
                  type="accountCategory"
                  :filter="true"
                  :multiple="true"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <autocomplete
                  v-model="branch"
                  title="branch"
                  type="branch"
                  :filter="true"
                  :multiple="true"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <autocomplete
                  v-model="entrepot"
                  title="entrepot"
                  type="entrepot"
                  :filter="true"
                  :multiple="true"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-select
                  v-model="lockStatus"
                  :label="$t('lockStatusDocuments')"
                  :items="lockStatusItems"
                  height="30"
                  dense
                  outlined
                  clearable
                  item-value="value"
                  item-text="text"
                />
              </v-col>
              <v-col md="4">
                <autocomplete
                  v-model="registerar"
                  :title="$t('registerar')"
                  type="partners"
                  :multiple="true"
                  :filter="true"
                />
              </v-col>
              <v-col md="4">
                <v-select
                  v-model="payedStatus"
                  :label="$t('payed-status')"
                  :items="payedStatuses"
                  height="30"
                  dense
                  outlined
                  clearable
                  item-value="value"
                  item-text="label"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <template>
                  <v-text-field
                    id="fromTime"
                    v-model="fromTime"
                    :label="$t('from-hour')"
                    outlined
                    dense
                    clearable
                    readonly
                  />
                  <date-picker
                    v-model="fromTime"
                    element="fromTime"
                    type="time"
                    :locale="$store.state.date_locale"
                    :format="'HH:mm'"
                    :display-format="'HH'"
                    auto-submit
                    :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                    @close="setDate"
                  />
                </template>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <template>
                  <v-text-field
                    id="toTime"
                    v-model="toTime"
                    :label="$t('to-hour')"
                    outlined
                    dense
                    clearable
                    readonly
                  />
                  <date-picker
                    v-model="toTime"
                    element="toTime"
                    type="time"
                    :locale="$store.state.date_locale"
                    :format="'HH:mm'"
                    :display-format="'HH'"
                    auto-submit
                    :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                    @close="setDate"
                  />
                </template>
              </v-col>
            </v-row>
            <v-row
              v-if="!isMobile && customFieldsPermission"
              class="mx-0"
            >
              <custom-fields
                v-model="customFields"
                :value="customFields"
                type-prop="invoiceCustomFields"
                :multiple="true"
                class="px-4"
              />
            </v-row>
            <v-card-actions class="px-3">
              <v-row class="ma-0">
                <v-col
                  cols="6"
                  class="pa-0 py-1"
                >
                  <v-btn
                    class=" mx-0"
                    color="primary"
                    @click="getItems"
                  >
                    {{ $t('filter') }}
                  </v-btn>
                </v-col>
                <v-col
                  cols="6"
                  class="pa-0 py-1"
                >
                  <v-btn
                    color="grey"
                    outlined
                    class="float-left mx-0"
                    @click="filterDialog = false"
                  >
                    {{ $t('cancel') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import General from '../../../../Mixins/general'

export default {
  data () {
    return {
      columnDefs: null,
      rowData: null,
      totalRows: null,
      lastPage: null,
      loading: false,
      invoiceIds: null,
      filterDialog: false,
      toDate: null,
      optionsNew: {
        page: 1,
        itemsPerPage: 10
      },
      account: null,
      sellStatus: null,
      currencyFilter: null,
      fromAmount: null,
      fromNumber: null,
      fromDate: null,
      toAmount: null,
      toNumber: null,
      toDate: null,
      tag: null,
      category: null,
      branch: null,
      entrepot: null,
      lockStatus: null,
      registerar: null,
      payedStatus: null,
      fromTime: null,
      toTime: null,
      customFields: null,
      initialLoading: true,
      paginationLoading: false
    }
  },
  mixins: [General],
  mounted () {
    this.columnDefs = [
      {
        headerName: '',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        // cellRenderer: 'AgVMenu',
        // cellRendererParams: (params) => ({
        //   items: [
        //     {
        //       label: this.$t('remove'),
        //       icon: 'mdi-trash-can-outline',
        //       iconColor: 'error',
        //       action: (rowData, vm) => vm.openDeleteDialog(rowData)
        //     }
        //   ]
        // }),
        resizable: false,
        pinned: 'right'
      },
      {
        headerName: '#',
        colId: 'rowNumber',
        valueGetter: params => params.node.rowIndex + 1,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        resizable: false,
        pinned: 'right'
      },
      {
        headerName: this.$t('product-id'),
        field: 'product.id',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params.data && params.data.product && params.data.product.id) {
            return '#' + params.data.product.id
          } else {
            return this.$t('haveNot')
          }
        },
        filterParams: {
          closeOnApply: true,
          buttons: ['apply', 'reset'],
          debounceMs: 0
        },
        width: 130,
        sortable: true
      },
      {
        headerName: this.$t('product-name'),
        field: 'product.name',
        filter: 'agTextColumnFilter',
        valueGetter: (params) => {
          if (params.data && params.data.product && params.data.product.name) {
            return params.data.product.name
          } else {
            return this.$t('haveNot')
          }
        },
        filterParams: {
          closeOnApply: true,
          buttons: ['apply', 'reset'],
          debounceMs: 0
        },
        minWidth: 400,
        maxWidth: 500,
        sortable: true
      },
      {
        headerName: this.$t('invoice-id'),
        field: 'invoice.id',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params.data && params.data.invoice && params.data.invoice.id) {
            return '#' + params.data.invoice.id
          } else {
            return this.$t('haveNot')
          }
        },
        sortable: true
      },
      {
        headerName: this.$t('register-date-invoice'),
        field: 'invoice.date',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params.data && params.data.invoice && params.data.invoice.date) {
            return params.data.invoice.date
          } else {
            return this.$t('haveNot')
          }
        },
        sortable: true
      },
      {
        headerName: this.$t('product-count-in-invoice'),
        field: 'invoice_count',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params.data && params.data.invoice_count) {
            return params.data.invoice_count
          } else {
            return 0
          }
        },
        sortable: true
      },
      {
        headerName: this.$t('product-count-in-entrepot'),
        field: 'entrepot_count',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params.data && params.data.entrepot_count) {
            return params.data.entrepot_count
          } else {
            return 0
          }
        },
        sortable: true
      },
      {
        headerName: this.$t('warehouse-remittances'),
        field: 'entrepot_ids',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params.data && params.data.entrepot_ids) {
            return params.data.entrepot_ids
          } else {
            return this.$t('haveNot')
          }
        },
        sortable: true
      }
    ]
  },
  computed: {
    payedStatuses () {
      return [
        { label: this.$t('payed'), value: 'payed' },
        { label: this.$t('not-payed'), value: 'notPayed' }
      ]
    }
  },
  components: {
    Status: () => import('../../components/general/Status/status')
  },
  beforeMount () {
    this.getItems()
  },
  methods: {
    getItems (isPaginate = false) {
      if (!isPaginate) {
        this.initialLoading = true
      }
      this.paginationLoading = isPaginate

      var app = this
      app.loading = true
      app.$axios.get(this.$endpoint + 'reports/product-without-entrepot', {
        params: {
          type: 'both',
          options: app.optionsNew,
          invoice_ids: app.invoiceIds,
          account: app.account,
          status: app.sellStatus,
          currency: app.currencyFilter,
          fromAmount: app.fromAmount,
          fromNumber: app.fromNumber,
          fromDate: app.fromDate,
          toAmount: app.toAmount,
          toNumber: app.toNumber,
          toDate: app.toDate,
          tag: app.tag,
          category: app.category,
          branch: app.branch,
          entrepot: app.entrepot,
          lockStatus: app.lockStatus,
          registerar: app.registerar,
          payedStatus: app.payedStatus,
          fromTime: app.fromTime,
          toTime :app.toTime,
          customFields: app.customFields
        }
      })
        .then(r => {
          r.data.data.mismatches.forEach((row, index) => {
            row.id = `${index}-${Date.now()}`;
          })
          app.rowData = [...r.data.data.mismatches]
          app.totalRows = r.data.meta.total
          app.lastPage = r.data.meta.last_page
        })
        .catch(err => {
          app.serverErrorHandeler(err)
        })
        .finally(() => {
          app.initialLoading = false
          app.paginationLoading = false
        })
    },
    updatePagination (value) {
      this.optionsNew.page = value
      this.getItems(true)
    },
    updateItemPerPage (value) {
      this.optionsNew.itemsPerPage = value
      this.optionsNew.page = 1
      this.getItems(true)
    }
  }
}
</script>

<style>
.v-card.wrapper {
  height: calc(100vh - 94px);
  display: flex;
  flex-direction: column;
}
.v-skeleton-loader__list-item.v-skeleton-loader__bone {
  align-content: unset;
  align-items: flex-start;
  padding: 0;
}
.theme--light.v-skeleton-loader .v-skeleton-loader__text {
  background: rgb(146 139 139 / 12%);
}
.v-skeleton-loader__text {
  height: 30px;
}
.v-skeleton-loader__list-item {
  height: 41px;
}
</style>
